import React, { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, Activity, Battery, Droplets, RefreshCw, Zap } from 'lucide-react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/data/store/utils/redux';
import { getNaerasDashboardAsync, getNaerasSettingsAsync } from '@/data/store/actions';
import { getNaerasDashboard, getNaerasSettings } from '@/data/store/selectors';
import { parseNaerasError, wetSeasonWindowLabel } from '@/pages/Calculate/utils/naerasBid';
import EnergyStatsCard from './EnergyStatsCard';
import SystemStatus from './SystemStatus';
import NaerasHybridFeatureCard from './NaerasHybridFeatureCard';
import OfferPanel from './OfferPanel';
import OutflowsPanel from './OutflowsPanel';
import LevelsPanel from './LevelsPanel';
import ReservoirPanel from './ReservoirPanel';
import PoolBridgePanel from './PoolBridgePanel';
import PumpingPanel from './PumpingPanel';
import PeziWaterPanel from './PeziWaterPanel';
import { MISSING, athensHourMinute, hourLabel, isNum, num, pct, shortDate } from '../utils/format';

/**
 * The live parts change once an hour, when ACROSS closes the previous one, and
 * the backend caches for 60 s — polling any faster only returns the same body.
 */
const POLL_MS = 90_000;

const sum = (values: number[] | undefined) =>
  (values ?? []).reduce((total, value) => total + (Number(value) || 0), 0);

function changeAgainst(current: number, previous: number | null | undefined, label: string) {
  if (!isNum(previous) || previous === 0) {
    return { change: `Χωρίς σύγκριση (${label})`, changeType: 'positive' as const };
  }

  const change = ((current - previous) / previous) * 100;

  return {
    change: `${change >= 0 ? '+' : ''}${num(change, 1)}% vs ${label}`,
    changeType: (change >= 0 ? 'positive' : 'negative') as 'positive' | 'negative',
  };
}

const DashboardContent: React.FC = () => {
  const dispatch = useAppDispatch();
  const dashboard = useSelector(getNaerasDashboard);
  const settings = useSelector(getNaerasSettings);

  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchDashboard = useCallback(
    () =>
      dispatch(getNaerasDashboardAsync({ silent: true }))
        .unwrap()
        .then(() => setError(null))
        .catch((reason) => {
          setError(parseNaerasError(reason).message ?? 'Τα δεδομένα του πίνακα δεν είναι διαθέσιμα.');
        }),
    [dispatch]
  );

  // Only for the season window in the labels; the season itself always comes
  // from the dashboard, never from the switch
  useEffect(() => {
    dispatch(getNaerasSettingsAsync({ silent: true }))
      .unwrap()
      .catch(() => undefined);
  }, [dispatch]);

  useEffect(() => {
    fetchDashboard();
    const timer = setInterval(fetchDashboard, POLL_MS);

    return () => clearInterval(timer);
  }, [fetchDashboard]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchDashboard();
    setIsRefreshing(false);
  };

  const splitHour = dashboard?.split_hour ?? null;
  const offer = dashboard?.offer.available ? dashboard.offer : null;
  const outflows = dashboard?.outflows.available ? dashboard.outflows : null;
  const pool = dashboard?.pool.available ? dashboard.pool : null;
  const reservoir = dashboard?.reservoir.available ? dashboard.reservoir : null;
  const pumping = dashboard?.pumping.available ? dashboard.pumping : null;

  // Today's offer against yesterday's locked one, which §2.2 already carries
  const yesterdayLabel = outflows ? shortDate(outflows.reconciled_date) : 'χθες';
  const todayHydro = offer ? sum(offer.program.Ano_Proespera) + sum(offer.program.Kato_Proespera) : 0;
  const todayHybrid = offer ? sum(offer.program.Hybrid_12h) : 0;
  const hydroComparison = changeAgainst(todayHydro, outflows?.offers.hydro_mwh, yesterdayLabel);
  const hybridComparison = changeAgainst(todayHybrid, outflows?.offers.hybrid_mwh, yesterdayLabel);

  const poolTrust = !pool
    ? { label: 'Μη διαθέσιμο', changeType: 'negative' as const }
    : pool.pool_source === 'reconciled'
    ? { label: 'Εκκαθαρισμένο 00:00', changeType: 'positive' as const }
    : pool.pool_source === 'manual'
    ? { label: 'Χειροκίνητη τιμή 00:00', changeType: 'positive' as const }
    : pool.pool_source === 'stale'
    ? { label: 'Εκκρεμεί εκκαθάριση', changeType: 'negative' as const }
    : { label: 'Αρχική τιμή', changeType: 'negative' as const };

  const reservoirToday = reservoir?.days[reservoir.days.length - 1];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50/50 via-white to-blue-50/30 px-6 py-8">
      <div className="mx-auto max-w-7xl">
        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12">
            <NaerasHybridFeatureCard />
          </div>

          {/* When the live estimate was computed, and anything holding it back */}
          <div className="col-span-12 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-gray-500">
                {dashboard
                  ? `Ημέρα ${shortDate(dashboard.date)} · ενημερώθηκε στις ${athensHourMinute(dashboard.generated_at)}`
                  : 'Φόρτωση δεδομένων…'}
              </p>
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 disabled:opacity-50"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                Ανανέωση
              </button>
            </div>

            {/* Every panel failed at once (502): nothing to draw */}
            {error && !dashboard && (
              <div className="flex items-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                {error}
              </div>
            )}

            {dashboard?.split_gap && (
              <div className="flex items-center gap-2 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
                Οι μετρήσεις της {hourLabel(dashboard.split_gap.hour)} δεν έχουν έρθει — η εκτίμηση είναι
                από τις {hourLabel(dashboard.split_gap.hour)}.
              </div>
            )}
          </div>

          <div className="col-span-6 lg:col-span-3">
            <EnergyStatsCard
              title="Υδροηλεκτρική Προσφορά"
              value={offer ? num(todayHydro, 1) : MISSING}
              unit="MWh σήμερα"
              change={offer ? hydroComparison.change : 'Δεν υπάρχει προσφορά'}
              changeType={hydroComparison.changeType}
              icon={Zap}
              gradient
              delay={0.1}
            />
          </div>

          <div className="col-span-6 lg:col-span-3">
            <EnergyStatsCard
              title="Υβριδική Προσφορά"
              value={offer ? num(todayHybrid, 1) : MISSING}
              unit="MWh (2x12ωρα)"
              change={offer ? hybridComparison.change : 'Δεν υπάρχει προσφορά'}
              changeType={hybridComparison.changeType}
              icon={Activity}
              delay={0.2}
            />
          </div>

          <div className="col-span-6 lg:col-span-3">
            <EnergyStatsCard
              title="Απόθεμα Hybrid Pool"
              value={num(pool?.today_00_mwh, 2)}
              unit="MWh"
              change={poolTrust.label}
              changeType={poolTrust.changeType}
              icon={Battery}
              delay={0.3}
            />
          </div>

          <div className="col-span-6 lg:col-span-3">
            <EnergyStatsCard
              title="Ταμιευτήρας Προεσπέρας"
              value={num(reservoirToday?.energy_mwh, 2)}
              unit="MWh ενέργεια"
              change={
                reservoirToday
                  ? `${pct(reservoirToday.volume_m3, reservoir?.capacity_m3)} χωρητικότητας`
                  : 'Μη διαθέσιμο'
              }
              changeType="positive"
              icon={Droplets}
              delay={0.4}
            />
          </div>

          <div className="col-span-12">
            <OfferPanel panel={dashboard?.offer} />
          </div>

          <div className="col-span-12 lg:col-span-8">
            <OutflowsPanel
              panel={dashboard?.outflows}
              seasonWindow={wetSeasonWindowLabel(settings?.window)}
            />
          </div>

          <div className="col-span-12 lg:col-span-4">
            <SystemStatus />
          </div>

          <div className="col-span-12">
            <LevelsPanel panel={dashboard?.levels} ratio={dashboard?.ratio} splitHour={splitHour} />
          </div>

          <div className="col-span-12">
            <ReservoirPanel panel={dashboard?.reservoir} />
          </div>

          <div className="col-span-12 xl:col-span-7">
            <PoolBridgePanel
              panel={dashboard?.pool}
              splitHour={splitHour}
              pumpedTodayMwh={pumping?.total_mwh ?? null}
            />
          </div>

          <div className="col-span-12 xl:col-span-5">
            <PumpingPanel panel={dashboard?.pumping} splitHour={splitHour} />
          </div>

          {/* Older backends don't send the panel at all — leave the card out */}
          {dashboard?.pezi_water && (
            <div className="col-span-12">
              <PeziWaterPanel panel={dashboard.pezi_water} today={dashboard.date} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardContent;
